"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const comparisonCards = [{
  title: "Consider a New Mechanical Bull When",
  badge: "Factory New",
  description: "You want a known starting condition and current product information for the machine you are purchasing. Review the individual listing for included equipment, specifications and whatever warranty or support actually applies.",
  ctaText: "Compare New Bulls",
  ctaLink: "/NewMechanicalBullsPage",
  primary: true
}, {
  title: "Consider a Used Mechanical Bull When",
  badge: "Pre-Owned",
  description: "You want to reduce the initial purchase cost and enough information is available to evaluate the exact unit's condition, controller, inflatable, included equipment and known history.",
  ctaText: "Compare Used Bulls",
  ctaLink: "/UsedMechanicalBullsPage",
  primary: false
}];
export default function Decide() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="new-vs-used-how-to-decide" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 md:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeIn} className="border-b border-border pb-8 md:pb-12">
          <div className="max-w-3xl">
            <span className="font-mono text-sm tracking-wider uppercase text-muted-foreground block mb-2">
              Equipment Comparison
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
              New vs Used Mechanical Bulls
            </h2>
            <p className="font-sans text-base md:text-lg text-muted-foreground mt-4 leading-relaxed">
              Neither new nor used equipment is automatically the better choice. The right option depends on your budget, desired starting condition and the information available for the exact machine.
            </p>
          </div>
        </motion.div>

        {/* Two-Column Comparison Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {comparisonCards.map((card, i) => <motion.div key={card.title} data-index={i} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn} className="border border-border bg-card p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <span className="font-mono text-xs uppercase tracking-wider bg-secondary text-secondary-foreground px-2.5 py-1">
                    {card.badge}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    Option 0{i + 1}
                  </span>
                </div>

                <h3 className="font-serif text-2xl md:text-3xl font-bold uppercase tracking-wide text-foreground mb-4">
                  {card.title}
                </h3>

                <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                  {card.description}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <Button asChild variant={card.primary ? "default" : "outline"} className="w-full sm:w-auto font-serif uppercase tracking-wider font-semibold">
                  <Link to={card.ctaLink} className="inline-flex items-center justify-center gap-2">
                    <span>{card.ctaText}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>)}
        </div>

        {/* Supporting Guidance */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeIn} className="mt-8 md:mt-10 border border-border bg-muted/40 p-6 md:p-8">
          <p className="font-sans text-sm md:text-base text-foreground leading-relaxed">
            A used machine with clear condition information can be easier to evaluate than a cheaper unit with an unknown history. Likewise, a new machine should still be compared by package contents rather than assumed to include everything you need.
          </p>
        </motion.div>
      </div>
    </section>;
}